import { useRouterState } from "@tanstack/react-router";
import { toast } from "@wags/ui/sonner.tsx";
import {
    Children,
    cloneElement,
    type MouseEventHandler,
    type ReactElement,
} from "react";
import {
    GOOGLE_SIGN_IN_START_FAILURE,
    useGoogleSignInStart,
} from "@/lib/core/auth-protocol";
import { withDefaultAuthRedirect } from "./auth-redirect";
import type { ResponsiveSignInOptions } from "./responsive-auth.interface";

type TriggerProps = {
    onClick?: MouseEventHandler;
};

type ResponsiveSignInButtonProps = ResponsiveSignInOptions & {
    children: ReactElement<TriggerProps>;
};

function useCurrentAuthRedirectUrl() {
    return useRouterState({
        select: (state) => state.location.href,
    });
}

export function ResponsiveSignInButton({
    children,
    ...options
}: ResponsiveSignInButtonProps) {
    const currentRedirectUrl = useCurrentAuthRedirectUrl();
    const { fallbackRedirectUrl } = withDefaultAuthRedirect(
        options,
        currentRedirectUrl,
    );
    const googleSignIn = useGoogleSignInStart({
        onFailure: () => toast.error(GOOGLE_SIGN_IN_START_FAILURE),
    });
    const child = Children.only(children);

    return cloneElement(child, {
        onClick: (event) => {
            child.props.onClick?.(event);
            if (event.defaultPrevented) return;
            void googleSignIn.start(fallbackRedirectUrl);
        },
    });
}
